import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  FaChartLine,
  FaSyncAlt,
  FaPlay,
  FaWhatsapp,
  FaExclamationCircle,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { useToast } from "../../components/Toast";
import Loader from "../../components/Loader";
import EmptyState from "../../components/EmptyState";
import DataTable from "../../components/DataTable";
import KPICard from "../../components/KPICard";
import StatusBadge from "../../components/StatusBadge";
import {
  estadoPauta,
  refrescarPauta,
  correrCicloPauta,
  guardarConfigPauta,
  revisarAccionPauta,
  asignarAnguloAd,
} from "../../utils/habiApi";
import {
  colors,
  spacing,
  typography,
  radius,
  pageContainer,
  pageTitle,
  pageSubtitle,
  cardBase,
  btnPrimary,
  btnSecondary,
} from "../../utils/designTokens";

/**
 * Gestor de Pauta (SuperAdmin) — hack 1 del plan de marketing.
 *
 * Muestra la métrica que manda (costo por conversación de WhatsApp por anuncio,
 * por ángulo y por público), las decisiones del gestor (con dry-run/kill-switch)
 * y las acciones esperando OK (follow-ups, cambios de presupuesto).
 *
 * TODO lo que se ve sale de los caches de Firestore vía /api/pauta/status —
 * el polling JAMÁS le pega a la Graph API (regla del plan). "Actualizar datos"
 * fuerza el refresh server-side.
 */

const ANGULOS_OPCIONES = {
  edificios: ["racional-plata", "no-te-responden", "ya-lo-pagas", "cambiar-es-facil", "prueba-social"],
  administradoras: ["el-laburo-de-atras", "segui-siendo-la-cara", "que-el-cierre-salga-solo", "crece-sin-contratar"],
  desarrolladoras: ["entrega-profesional", "amenity-invisible"],
};

const fmtUsd = (v) =>
  typeof v === "number" ? `US$ ${v.toLocaleString("es-UY", { maximumFractionDigits: 2 })}` : "—";

const PautaMarketing = () => {
  const { showToast } = useToast();
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  const refrescar = useCallback(async () => {
    try {
      const data = await estadoPauta();
      setEstado(data);
    } catch (e) {
      console.error("estadoPauta error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Polling cada 10s sobre los caches (patrón del organigrama: REST, no onSnapshot).
  useEffect(() => {
    refrescar();
    timerRef.current = setInterval(refrescar, 10000);
    return () => clearInterval(timerRef.current);
  }, [refrescar]);

  const config = estado?.config || {};
  const cache = estado?.cache || {};
  const ads = useMemo(() => cache.ads || [], [cache]);
  const acciones = estado?.acciones || [];
  const pendientes = acciones.filter((a) => a.resultado === "esperando_ok");
  const cambiosActivos = estado?.activos_cambios?.ultimos || [];

  const kpis = useMemo(() => {
    const spend = ads.reduce((s, a) => s + (Number(a.spend) || 0), 0);
    const conv = ads.reduce((s, a) => s + (Number(a.conversaciones) || 0), 0);
    const calif = ads.reduce((s, a) => s + (Number(a.leads_calificados) || 0), 0);
    return { spend, conv, costoConv: conv ? spend / conv : null, calif };
  }, [ads]);

  const conAccion = useCallback(
    async (fn, okMsg) => {
      if (busy) return;
      setBusy(true);
      try {
        await fn();
        if (okMsg) showToast(okMsg, "success");
        await refrescar();
      } catch (e) {
        showToast(e.message || "Algo falló", "error");
      } finally {
        setBusy(false);
      }
    },
    [busy, refrescar, showToast]
  );

  if (loading) return <Loader />;

  const modoLabel = config.kill_switch
    ? "KILL-SWITCH: solo datos, no decide"
    : config.dry_run
      ? "DRY-RUN: decide y audita, no aplica"
      : "ACTIVO: aplica con topes";

  return (
    <div style={pageContainer}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: spacing[3], marginBottom: spacing[4] }}>
        <div>
          <h1 style={pageTitle}>
            <FaChartLine style={{ marginRight: 8, color: colors.primary }} /> Pauta
          </h1>
          <p style={{ ...pageSubtitle, maxWidth: 760, marginTop: 4 }}>
            Costo por conversación real por anuncio, ángulo y público — y las decisiones del gestor.
          </p>
          <p style={{ fontSize: typography.sizes.sm, color: config.kill_switch || config.dry_run ? colors.warningDark : colors.successDark, margin: "6px 0 0", fontWeight: 400 }}>
            {modoLabel}
          </p>
        </div>
        <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
          <button
            style={btnSecondary}
            className="habitia-btn-secondary"
            disabled={busy}
            onClick={() => conAccion(refrescarPauta, "Datos actualizados desde Meta")}
          >
            <FaSyncAlt /> Actualizar datos
          </button>
          <button
            style={btnPrimary}
            className="habitia-btn-primary"
            disabled={busy}
            onClick={() => conAccion(() => correrCicloPauta("semanal"), "Ciclo semanal corrido")}
          >
            <FaPlay /> Correr ciclo semanal
          </button>
        </div>
      </div>

      {/* Modo seguro: toggles */}
      <div style={{ ...cardBase, padding: spacing[4], marginBottom: spacing[6], display: "flex", gap: spacing[6], flexWrap: "wrap", alignItems: "center" }}>
        <ToggleConfig
          label="Kill-switch (no decidir nada)"
          value={config.kill_switch !== false}
          disabled={busy}
          onChange={(v) => conAccion(() => guardarConfigPauta({ kill_switch: v }), v ? "Kill-switch ACTIVADO" : "Kill-switch apagado — el gestor decide")}
        />
        <ToggleConfig
          label="Dry-run (decidir sin aplicar en Meta)"
          value={config.dry_run !== false}
          disabled={busy}
          onChange={(v) => conAccion(() => guardarConfigPauta({ dry_run: v }), v ? "Dry-run activado" : "Dry-run apagado — APLICA en Meta")}
        />
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
          Topes: ±{config.cambio_max_pct_dia || 20}%/día · máx {fmtUsd(config.presupuesto_diario_max_usd)}/día · pausa con {config.min_conversaciones_para_actuar || 5}+ conversaciones de dato
        </span>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4], marginBottom: spacing[6] }}>
        <KPICard icon={FaChartLine} label={`Inversión (${cache.ventana || "7d"})`} value={fmtUsd(kpis.spend)} />
        <KPICard icon={FaWhatsapp} iconBg={colors.successLight} iconColor={colors.success} label="Conversaciones" value={String(kpis.conv)} />
        <KPICard icon={FaChartLine} label="Costo / conversación" value={kpis.costoConv ? fmtUsd(kpis.costoConv) : "—"} />
        <KPICard icon={FaCheck} iconBg={colors.successLight} iconColor={colors.success} label="Leads calificados" value={String(kpis.calif)} />
      </div>

      {/* Esperando OK */}
      {pendientes.length > 0 && (
        <div style={{ marginBottom: spacing[8] }}>
          <h2 style={{ fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400, marginBottom: spacing[3] }}>
            <FaExclamationCircle style={{ color: colors.warning, marginRight: 6 }} />
            Esperando tu OK ({pendientes.length})
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
            {pendientes.map((a) => (
              <div key={a.id} style={{ ...cardBase, padding: spacing[4], display: "flex", justifyContent: "space-between", gap: spacing[3], flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ minWidth: 260, flex: 1 }}>
                  <div style={{ fontSize: typography.sizes.md, color: colors.gray900 }}>
                    {a.tipo === "followup" ? "Follow-up a " : `${a.tipo} → `}
                    {a.target_name || a.lead_id || a.target_id}
                    {a.template ? ` · template ${a.template}` : ""}
                  </div>
                  <div style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginTop: 2 }}>{a.razon}</div>
                </div>
                <div style={{ display: "flex", gap: spacing[2] }}>
                  <button
                    style={btnPrimary}
                    className="habitia-btn-primary"
                    disabled={busy}
                    onClick={() => conAccion(() => revisarAccionPauta(a.id, "aprobar"), "Acción aplicada")}
                  >
                    <FaCheck /> Aprobar
                  </button>
                  <button
                    style={btnSecondary}
                    className="habitia-btn-secondary"
                    disabled={busy}
                    onClick={() => conAccion(() => revisarAccionPauta(a.id, "descartar"), "Acción descartada")}
                  >
                    <FaTimes /> Descartar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cambios en los activos */}
      {cambiosActivos.length > 0 && (
        <div style={{ ...cardBase, padding: spacing[4], marginBottom: spacing[6], borderLeft: `3px solid ${colors.info}` }}>
          <div style={{ fontSize: typography.sizes.sm, color: colors.gray900, marginBottom: 4 }}>
            Cambios detectados en los activos (web/planes):
          </div>
          {cambiosActivos.slice(0, 5).map((c, i) => (
            <div key={i} style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>· {c}</div>
          ))}
        </div>
      )}

      {/* Por anuncio */}
      <h2 style={{ fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400, marginBottom: spacing[3] }}>Por anuncio ({cache.ventana || "7d"})</h2>
      <div style={{ marginBottom: spacing[8] }}>
        <DataTable
          columns={[
            { key: "ad_name", label: "Anuncio", render: (v) => <span style={{ fontSize: typography.sizes.sm }}>{v || "—"}</span> },
            {
              key: "angulo",
              label: "Público / Ángulo",
              render: (v, row) =>
                v ? (
                  <span style={{ fontSize: typography.sizes.sm }}>{row.publico || "?"} · {v}</span>
                ) : (
                  <AsignarAngulo
                    adId={row.ad_id}
                    disabled={busy}
                    onAsignar={(publico, angulo) =>
                      conAccion(() => asignarAnguloAd(row.ad_id, publico, angulo), "Ángulo asignado")}
                  />
                ),
            },
            { key: "spend", label: "Inversión", align: "right", render: (v) => fmtUsd(Number(v) || 0) },
            { key: "conversaciones", label: "Conv.", align: "right" },
            { key: "costo_conv", label: "US$/conv", align: "right", render: (v) => (v ? fmtUsd(v) : "—") },
            { key: "leads_calificados", label: "Calificados", align: "right" },
            { key: "ctr", label: "CTR", align: "right", render: (v) => (v ? `${Number(v).toFixed(2)}%` : "—") },
            { key: "frequency", label: "Freq.", align: "right", render: (v) => (v ? Number(v).toFixed(1) : "—") },
          ]}
          data={ads}
          emptyIcon={FaChartLine}
          emptyTitle="Sin datos de pauta"
          emptyDescription='Corré "Actualizar datos" (requiere el token de Meta con ads_read en el backend) o revisá que haya campañas activas.'
        />
      </div>

      {/* Por ángulo */}
      <h2 style={{ fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400, marginBottom: spacing[3] }}>Por ángulo</h2>
      <div style={{ marginBottom: spacing[8] }}>
        <DataTable
          columns={[
            { key: "key", label: "Público / Ángulo" },
            { key: "spend", label: "Inversión", align: "right", render: (v) => fmtUsd(Number(v) || 0) },
            { key: "conversaciones", label: "Conv.", align: "right" },
            { key: "costo_conv", label: "US$/conv", align: "right", render: (v) => (v ? fmtUsd(v) : "—") },
            { key: "leads_calificados", label: "Calificados", align: "right" },
            { key: "ads", label: "Ads", align: "right" },
          ]}
          data={Object.entries(cache.por_angulo || {}).map(([key, g]) => ({ key, ...g }))}
          emptyIcon={FaChartLine}
          emptyTitle="Sin agregados por ángulo"
          emptyDescription="Se arman solos cuando los ads siguen la convención HAB-{publico}-{angulo}-... o les asignás ángulo a mano en la tabla de arriba."
        />
      </div>

      {/* Historial de acciones */}
      <h2 style={{ fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400, marginBottom: spacing[3] }}>Decisiones del gestor</h2>
      {acciones.length === 0 ? (
        <EmptyState
          icon={FaChartLine}
          title="Sin decisiones todavía"
          description="Cuando corra el ciclo (Cloud Scheduler diario/semanal o el botón de arriba), cada decisión queda auditada acá."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[2], marginBottom: spacing[8] }}>
          {acciones.slice(0, 25).map((a) => (
            <div key={a.id} style={{ ...cardBase, padding: spacing[3], display: "flex", justifyContent: "space-between", gap: spacing[3], flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ minWidth: 240, flex: 1 }}>
                <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
                  {a.tipo} · {a.target_name || a.lead_id || a.target_id || "—"}
                </span>
                <div style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>{a.razon}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
                <span style={{ fontSize: 11, color: colors.gray400 }}>{String(a.ts || "").slice(0, 16).replace("T", " ")}</span>
                <StatusBadge status={a.resultado || "pendiente"} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Guía operativa */}
      <div style={{ ...cardBase, padding: spacing[5] }}>
        <h2 style={{ fontSize: typography.sizes.md, color: colors.black, fontWeight: 400, marginTop: 0, marginBottom: spacing[2] }}>Guía operativa (Andrómeda)</h2>
        <div style={{ fontSize: typography.sizes.sm, color: colors.gray700, display: "flex", flexDirection: "column", gap: 6 }}>
          <span>· 1 campaña click-to-WhatsApp · 1 conjunto amplio · 4-6 creativos compitiendo, UNO POR ÁNGULO — Meta reparte cada ángulo a su audiencia. Nada de A/B de micro-variaciones.</span>
          <span>· Nombrá los ads <b style={{ fontWeight: 400, color: colors.gray900 }}>HAB-{"{publico}"}-{"{angulo}"}-{"{hook}"}-v1</b> (ej: HAB-admins-laburo-papelerio-v1) — o asignales ángulo a mano acá arriba.</span>
          <span>· El gestor corta sangría a diario y decide redistribución UNA vez por semana con mínimos de datos (nunca con n=2). Todo queda auditado.</span>
          <span>· Los guiones nuevos por ángulo se piden desde el Equipo de Marketing con el foco "matriz de anuncios de {"{publico}"} sobre {"{tema}"}".</span>
        </div>
      </div>
    </div>
  );
};

const ToggleConfig = ({ label, value, onChange, disabled }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: typography.sizes.sm, color: colors.gray900, cursor: disabled ? "default" : "pointer" }}>
    <input
      type="checkbox"
      checked={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ width: 16, height: 16, accentColor: colors.primary }}
    />
    {label}
  </label>
);

const AsignarAngulo = ({ adId, onAsignar, disabled }) => {
  const [publico, setPublico] = useState("");
  const opciones = publico ? ANGULOS_OPCIONES[publico] || [] : [];
  return (
    <span style={{ display: "inline-flex", gap: 6 }}>
      <select
        aria-label="Público del anuncio"
        value={publico}
        disabled={disabled}
        onChange={(e) => setPublico(e.target.value)}
        style={miniSelect}
      >
        <option value="">Público…</option>
        {Object.keys(ANGULOS_OPCIONES).map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      <select
        aria-label="Ángulo del anuncio"
        value=""
        disabled={disabled || !publico}
        onChange={(e) => e.target.value && onAsignar(publico, e.target.value)}
        style={miniSelect}
      >
        <option value="">Ángulo…</option>
        {opciones.map((a) => (
          <option key={a} value={a}>{a}</option>
        ))}
      </select>
    </span>
  );
};

const miniSelect = {
  padding: "4px 6px",
  fontSize: typography.sizes.sm,
  fontWeight: 400,
  border: `1px solid ${colors.gray200}`,
  borderRadius: radius.sm,
  background: colors.white,
  fontFamily: "inherit",
};

export default PautaMarketing;
